import { TFunction } from "i18next/typescript/t";
import { TElectricConnector, TStation, TStationConcept, TStationType } from "../types/station-types";
import { TStationFeature } from "../components/elements/station-feature-list/station-feature-list";
import dayjs from "dayjs";
import { PreemLogo, SaifaLogo, UnoXLogo, YxLogo } from "../components/elements/station-logos/station-logos";
import { formatTimeWithOptionalMinutes } from "../../../helpers/date-helper";

export const doesTimePeriodHaveOpeningHours = (startOpeningHours: string, stopOpeningHours: string): boolean => {
  if (!startOpeningHours && !stopOpeningHours) {
    return false;
  }

  return startOpeningHours || stopOpeningHours ? true : false;
};

export const getOpeningHoursStringFromTimePeriod = (
  startOpeningHours: string,
  stopOpeningHours: string,
  t: TFunction,
): string => {
  if (!startOpeningHours && !stopOpeningHours) {
    return t("CLOSED");
  }

  //stations that have starting opening hours and/or closing opening hours that are both 00:00 are open 24/7
  if (
    (startOpeningHours === "00:00" && stopOpeningHours === "") ||
    (startOpeningHours === "00:00" && stopOpeningHours === "00:00")
  ) {
    return t("FIND_STATION_OPEN_24_HOURS");
  }

  return `${formatTimeWithOptionalMinutes(startOpeningHours)}–${formatTimeWithOptionalMinutes(stopOpeningHours)}`;
};

export const doesStationHaveOpeningHours = (station: TStation): boolean => {
  if (station.openingHours) {
    const hasAnyOpeningHours =
      station.openingHours.openingHourWeekday ||
      station.openingHours.openingHourSaturday ||
      station.openingHours.openingHourSunday
        ? true
        : false;

    return hasAnyOpeningHours;
  }

  return false;
};

const getTodaysOpeningHoursForStation = (station: TStation) => {
  if (!doesStationHaveOpeningHours(station)) {
    return null;
  }

  const today = dayjs();

  switch (today.day()) {
    case 0:
      return { openingHour: station.openingHours.openingHourSunday, closeHour: station.openingHours.closeHourSunday };
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
      return { openingHour: station.openingHours.openingHourWeekday, closeHour: station.openingHours.closeHourWeekday };
    case 6:
      return {
        openingHour: station.openingHours.openingHourSaturday,
        closeHour: station.openingHours.closeHourSaturday,
      };

    default:
      return null;
  }
};

export const isStationMissingOpeningHoursButOpenAroundTheClock = (station: TStation): boolean => {
  // if a station that is automat or saifa has no opening hours it means that it is open 24/7
  if (station.stationType === "02 Automat" || station.stationType === "03 TRUCK") {
    if (!doesStationHaveOpeningHours(station)) {
      return true;
    }
  }

  return false;
};

export const getTodaysOpeningHoursForStationType = (station: TStation, t: TFunction) => {
  const isOpenAroundTheClock = isStationMissingOpeningHoursButOpenAroundTheClock(station);

  if (isOpenAroundTheClock) {
    return t("FIND_STATION_OPENING_HOURS_UNMANNED");
  }

  const todaysOpeningHours = getTodaysOpeningHoursForStation(station);

  return t("FIND_STATION_OPENING_HOURS_TODAY", {
    openingHours: todaysOpeningHours?.openingHour,
    closingHours: todaysOpeningHours?.closeHour,
  });
};

export const isStationManned = (station: TStation) => {
  switch (station.stationType) {
    case "01 Bemannad": {
      return true;
    }
    case "02 Automat": {
      return false;
    }

    default: {
      return false;
    }
  }
};

export const getLabelForStationType = (stationType: TStationType, t: TFunction) => {
  switch (stationType) {
    case "01 Bemannad": {
      return t("FIND_STATION_PREEM_MANNED");
    }
    case "02 Automat": {
      return t("FIND_STATION_PREEM_UNMANNED");
    }
    case "03 TRUCK": {
      return t("FIND_STATION_SAIFA_UNMANNED");
    }

    default: {
      return "Can't resolve stationType";
    }
  }
};

export const getLogoForStation = (stationType: TStationType, stationConcept: TStationConcept): React.ReactNode => {
  switch (stationType) {
    case "01 Bemannad":
    case "02 Automat": {
      if (stationConcept === "22 Uno X Truck" || stationConcept === "21 Uno X") {
        return <UnoXLogo />;
      }

      if (stationConcept === "20 Preem Truck YX") {
        return <YxLogo />;
      }

      return <PreemLogo />;
    }

    case "03 TRUCK": {
      return <SaifaLogo />;
    }

    default:
      return null;
  }
};

export const resolveFuelsForStation = (station: TStation, t: TFunction): Array<TStationFeature> => {
  /**
   * # ACPDiesel - på anläggningar vi inte äger lagret - ska kallas Diesel
   * # ACPDiesel50 - lyft med marknad, gammal data? Fel?
   * # ACPEvolutionDiesel - ACPDiesel50 borde vara denna?
   * # AdBlue
   * DATAFEL -> AdBlue27PreemEvolutionDiesel30Anlaggningsdiesel - Fel i PDP eller PSK? Ska inte visas
   * # Anlaggningsdiesel - Norsk grej - Anläggningsdiesel
   * # Bensin95 - Bensin 95 - Visa upp men inga filter, om det är i Sverige är det fel. Evolution Bensin 95.
   * # Bensin96 - Gammal, finns inte. Ska tas bort i PSK. Visa ej.
   * # Bensin98 - Special, bör finnas filter för.
   * # Biodiesel100 - Gammal, ska tas bort. Visa ej.
   * # EtanolE85 - Visa tillsvidare, på väg ut
   * # EvolutionBensin95 - Korrekt för Sverige
   * # Fordongas - Rätt
   * # HVO <- visa inte
   * # HVO100 <- visa inte
   * # HVO100LattTrafikPersonbil <- visa
   * # HVO100TungTrafik <- visa
   * # HVOLattTrafikPersonbil <- visa inte, och kolla upp hur många
   * # HVOTungTrafik <- visa inte,
   * # LBGFlytandeBiogas - Visas, för sopbilar osv
   * # MotorgasLPG - Visas ej, är fel och gammal.
   * # PreemEvolutionDiesel - Norska Diesel - ska stå Preem Evolution Diesel
   * # PreemEvolutionDieselPlus - Osäkerheter kring denna
   * DATAFEL -> PreemEvolutionDiesel30Anläggningsdiesel - Marcus kollar med Norge
   * # PreemEvolutionDieselB0 - Ska heta Evolution Diesel B0
   */
  const fuels: Array<TStationFeature> = [];

  if (station.fuelTypes) {
    for (const fuel of station.fuelTypes) {
      switch (fuel.name) {
        case "AdBlue": {
          fuels.push({
            id: "AdBlue",
            text: t("FIND_STATION_FUEL_AD_BLUE"),
          });
          break;
        }
        case "ACPDiesel": {
          fuels.push({
            id: "ACPDiesel",
            text: t("FIND_STATION_FUEL_ACP_DIESEL"),
          });
          break;
        }
        case "ACPDiesel50": {
          fuels.push({
            id: "ACPDiesel50",
            text: t("FIND_STATION_FUEL_ACP_DIESEL_50"),
          });
          break;
        }
        case "Anlaggningsdiesel": {
          fuels.push({
            id: "Anlaggningsdiesel",
            text: t("FIND_STATION_FUEL_ANLAGGNINGSDIESEL"),
          });
          break;
        }
        case "PreemEvolutionDiesel": {
          fuels.push({
            id: "PreemEvolutionDiesel",
            text: t("FIND_STATION_FUEL_PREEM_EVOLUTION_DIESEL"),
          });
          break;
        }
        case "PreemEvolutionDieselFriFranRME": {
          fuels.push({
            id: "PreemEvolutionDieselFriFranRME",
            text: t("FIND_STATION_FUEL_PREEM_EVOLUTION_DIESEL_FRI_FRAN_RME"),
          });
          break;
        }
        case "EvolutionBensin95": {
          fuels.push({
            id: "EvolutionBensin95",
            text: t("FIND_STATION_FUEL_EVOLUTION_BENSIN_95"),
          });
          break;
        }
        case "Bensin95": {
          fuels.push({
            id: "Bensin95",
            text: t("FIND_STATION_FUEL_BENSIN_95"),
          });
          break;
        }
        case "Bensin98": {
          fuels.push({
            id: "Bensin98",
            text: t("FIND_STATION_FUEL_BENSIN_98"),
          });
          break;
        }
        case "EtanolE85": {
          fuels.push({
            id: "EtanolE85",
            text: t("FIND_STATION_FUEL_ETANOL_E85"),
          });
          break;
        }
        case "PreemRedefineHVO100LattTrafikPersonbil":
        case "PreemRedefineHVO100LatttrafikPersonbil": {
          fuels.push({
            id: "PreemRedefineHVO100LattTrafikPersonbil",
            text: t("FIND_STATION_FUEL_PREEM_REDEFINE_HVO100_LIGHT_VEHICLE"),
          });
          break;
        }
        case "PreemRedefineHVO100TungTrafik":
        case "PreemRedefineHVO100Tungtrafik": {
          fuels.push({
            id: "PreemRedefineHVO100TungTrafik",
            text: t("FIND_STATION_FUEL_PREEM_REDEFINE_HVO100_HEAVY_VEHICLE"),
          });
          break;
        }
        case "HVO100LattTrafikPersonbil": {
          fuels.push({
            id: "HVO100LattTrafikPersonbil",
            text: t("FIND_STATION_FUEL_HVO100_LIGHT_VEHICLE"),
          });
          break;
        }
        case "HVO100TungTrafik": {
          fuels.push({
            id: "HVO100TungTrafik",
            text: t("FIND_STATION_FUEL_HVO100_HEAVY_VEHICLE"),
          });
          break;
        }
        case "LBGFlytandeBiogas": {
          fuels.push({
            id: "LBGFlytandeBiogas",
            text: t("FIND_STATION_FUEL_LBG_FLYTANDE_BIOGAS"),
          });
          break;
        }
        case "Fordongas": {
          fuels.push({
            id: "Fordongas",
            text: t("FIND_STATION_FUEL_FORDONGAS"),
          });
          break;
        }
      }
    }
  }

  return fuels;
};

const getLabelForElectricConnectorStandard = (connector: TElectricConnector, t: TFunction) => {
  if (!connector || !connector.standard) {
    return "";
  }

  switch (connector.standard.toLocaleLowerCase()) {
    case "ccs": {
      return t("FIND_STATION_FUEL_EV_CONNECTOR_STANDARD_CCS");
    }
    case "chademo": {
      return t("FIND_STATION_FUEL_EV_CONNECTOR_STANDARD_CHADEMO");
    }
    case "type2": {
      return t("FIND_STATION_FUEL_EV_CONNECTOR_STANDARD_TYPE2");
    }
    default:
      return "";
  }
};

export const resolveElectricConnectorsForStation = (station: TStation, t: TFunction): Array<TStationFeature> => {
  const electricConnectors = [];

  for (const connector of station.electricConnectors) {
    const connectorStandardLabel = getLabelForElectricConnectorStandard(connector, t);

    electricConnectors.push({
      id: connector.power,
      text: t("FIND_STATION_FUEL_EV_CHARGING", { power: connector.power, standard: connectorStandardLabel }),
    });
  }

  return electricConnectors;
};

export const resolveServicesForStation = (station: TStation, t: TFunction): Array<TStationFeature> => {
  const services: Array<TStationFeature> = [];

  if (station.foodAndBeverages) {
    services.push({
      id: "foodAndBeverages",
      text: t("FIND_STATION_SERVICE_FOOD_AND_BEVERAGES"),
    });
  }

  if (station.trailerRental) {
    services.push({
      id: "trailerRental",
      text: t("FIND_STATION_SERVICE_TRAILER_RENTAL"),
    });
  }

  if (station.carWashAutomatic) {
    services.push({
      id: "carWashAutomatic",
      text: t("FIND_STATION_SERVICE_CAR_WASH_AUTOMATIC"),
    });
  }

  if (station.carWashSelfService) {
    services.push({
      id: "carWashSelfService",
      text: t("FIND_STATION_SERVICE_CAR_WASH_SELF_SERVICE"),
    });
  }

  if (station.espressoHouse) {
    services.push({
      id: "espressoHouse",
      text: t("FIND_STATION_SERVICE_ESPRESSO_HOUSE"),
    });
  }

  if (station.adaptedFor24mVehicles) {
    services.push({
      id: "adaptedFor24mVehicles",
      text: t("FIND_STATION_SERVICE_ADAPTED_FOR_24M_VEHICLE"),
    });
  }

  return services;
};
